/**
 * "Last time" per exercise, from `GET /me/exercise-history` — PURE.
 *
 * The history is oldest first and spans sessions. What the log view shows
 * under each scrubber, and what the store's suggestions start from
 * (`setPrevious`, T53), is the most recent OTHER session's sets of that
 * exercise, in set order. Today's own sets are excluded by session id: once
 * the first set of today syncs, it is in the history too, and "last time"
 * would otherwise become the set just logged.
 *
 * A warm-up is not "last time" (`D209`): last time's first WORKING set is
 * what today's first working set is compared with and suggested from, so
 * the warm-ups are left out before the sets are numbered.
 */
import type { SetKind } from "@repo/contract/training";
import type { PreviousSet } from "../store/session-model";

export interface HistorySet {
  readonly exerciseId: string;
  readonly kind?: SetKind | null | undefined;
  readonly load: number | null;
  readonly performedAt: string;
  readonly reps: number | null;
  readonly sessionId: string;
  readonly setNumber: number;
}

export function previousSetsByExercise(
  sets: readonly HistorySet[],
  currentSessionId: string
): Record<string, PreviousSet[]> {
  const latest = new Map<string, { at: number; sessionId: string }>();
  for (const set of sets) {
    if (set.sessionId === currentSessionId || set.kind === "warmup") {
      continue;
    }
    const at = Date.parse(set.performedAt);
    const seen = latest.get(set.exerciseId);
    if (seen === undefined || at > seen.at) {
      latest.set(set.exerciseId, { at, sessionId: set.sessionId });
    }
  }
  const out: Record<string, PreviousSet[]> = {};
  for (const [exerciseId, { sessionId }] of latest) {
    out[exerciseId] = sets
      .filter(
        (s) =>
          s.exerciseId === exerciseId &&
          s.sessionId === sessionId &&
          s.kind !== "warmup"
      )
      .sort((a, b) => a.setNumber - b.setNumber)
      .map((s) => ({ load: s.load, reps: s.reps }));
  }
  return out;
}

/** A stable fingerprint, so the same history is not written to the store twice. */
export function previousKey(
  previous: Readonly<Record<string, readonly PreviousSet[]>>
): string {
  return JSON.stringify(
    Object.keys(previous)
      .sort()
      .map((id) => [id, previous[id]])
  );
}
